import { useMemo } from 'react';
import { Paper, Title, Text, Stack } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import type { DataLibrarySurveyRow } from '../../types';
import { SATISFACTION_ORDER, SATISFACTION_COLORS } from './useCoachDashboardData';
import { buildCoachStackedBars } from './chartUtils';

interface RecordCountByCoachChartProps {
  rows: DataLibrarySurveyRow[];
  onSegmentClick: (coach: string, rating: string) => void;
}

export function RecordCountByCoachChart({ rows, onSegmentClick }: RecordCountByCoachChartProps) {
  const data = useMemo(() => buildCoachStackedBars(rows), [rows]);

  const series = useMemo(
    () =>
      SATISFACTION_ORDER.map(name => ({
        name,
        color: SATISFACTION_COLORS[name] || '#888',
      })),
    [],
  );

  if (data.length === 0) {
    return (
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">Record count by coach</Title>
        <Text c="dimmed" ta="center" py="xl">No survey responses match these filters</Text>
      </Paper>
    );
  }

  return (
    <Paper p="lg" radius="md" withBorder>
      <Title order={4} mb="md">Record count by coach</Title>
      <BarChart
        h={350}
        data={data}
        dataKey="coach"
        type="stacked"
        series={series}
        withLegend
        legendProps={{ verticalAlign: 'top', align: 'right' }}
        barProps={(seriesItem) => ({
          onClick: (entry: Record<string, unknown>) => {
            const coach = entry.coach as string;
            if (coach) onSegmentClick(coach, seriesItem.name);
          },
          style: { cursor: 'pointer' },
        })}
        tooltipProps={{
          content: ({ payload, label }) => {
            if (!payload?.length) return null;
            const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
            return (
              <Paper p="sm" radius="sm" withBorder shadow="sm" style={{ backgroundColor: '#fff' }}>
                <Stack gap={4}>
                  <Text fw={600} size="sm">Medley Coach: {label}</Text>
                  {payload.filter(p => Number(p.value) > 0).map(p => (
                    <Text key={p.name} size="xs">
                      <span style={{ color: p.color as string }}>{'\u25CF'} </span>
                      {p.name}: {p.value} records
                    </Text>
                  ))}
                  <Text fw={600} size="xs" mt={4}>Total: {total} records</Text>
                </Stack>
              </Paper>
            );
          },
        }}
        xAxisProps={{ angle: -45, textAnchor: 'end', height: 100, interval: 0, tick: { fontSize: 10 } }}
      />
    </Paper>
  );
}
